import { cloneDeep } from "lodash";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../api";
import { conversationType } from "../config/Constant";
import { useSideBarStore } from "../store/SideBarStore";
import { useInfoUser } from "../store/UserStore";
import { searchOrganization } from "../utils/OrganizationHelper";
import { useAuthLayoutContext } from "./AuthLayoutContext";
import { useMenuContext } from "./MenuContext";

const DraftMailContext = createContext(null);
export const useDraftMailContext = () => {
  return useContext(DraftMailContext);
};

export const DraftMailProvider = ({
  children,
  letterId,
  senderId,
  replyMessage,
}) => {
  const {
    selectedConversation,
    setSelectedConversation,
    messageSearch,
    setIsModalAddMember,
  } = useMenuContext();

  const { orgList: originalOrgList } = useAuthLayoutContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const recipent = queryParams.get("recipent");

  const { user } = useInfoUser();
  const [isLoading, setIsLoading] = useState(false);
  const [lastMessageTs, setLastMessageTs] = useState(null);
  const [draftMessage, setDraftMessage] = useState({});
  const [topMessageList, setTopMessageList] = useState([]);
  const [isReply, setIsReply] = useState(false);
  const [cCOrgList, setCCOrgList] = useState([]);
  const [memberOrgList, setMemberOrgList] = useState([]);
  const [isNotification, setIsNotification] = useState(false);
  const [isModalLabelGroup, setIsModalLabelGroup] = useState(false);

  const [title, setTitle] = useState("");
  const [isOpenModalDeleteConversation, setIsOpenModalDeleteConversation] =
    useState(false);

  const [isReplyAll, setIsReplyAll] = useState(false);
  const [recipientIds, setRecipientIds] = useState([]);

  const init = async () => {
    if (!selectedConversation?.conversationId) {
      setDraftMessage({
        status: null,
        createdAt: null,
        updatedAt: null,
        requestUuid: uuidv4(),
        senderId: user?.userId,
        isDraft: true,
        isEditing: true,
        draftMail: {
          status: null,
          requestUuid: uuidv4(),
          senderId: user?.userId,
          content: "",
          files: [],
        },
      });
      return;
    }

    if (
      [conversationType.DRAFT, conversationType.SCHEDULE].includes(
        selectedConversation?.type,
      )
    ) {
      const response = await apiFactory.draftMessageApi.getMailDraft(
        selectedConversation.conversationId,
      );

      if (response?.status !== 200) return;

      const draftMail = response?.data;

      setDraftMessage({
        status: null,
        createdAt: null,
        updatedAt: null,
        requestUuid: draftMail?.requestUuid || uuidv4(),
        conversationId: selectedConversation?.conversationId,
        senderId: user?.userId,
        draftMailId: draftMail?.draftMailId,
        recipientIds: draftMail?.recipientIds,
        ccIds: draftMail?.ccIds,
        isDraft: true,
        isEditing: true,
        draftMail: {
          status: null,
          draftMailId: draftMail?.draftMailId,
          requestUuid: draftMail?.requestUuid || uuidv4(),
          conversationId: selectedConversation?.conversationId,
          senderId: user?.userId,
          recipientId: "ALL",
          content: draftMail?.content || "",
          files: draftMail?.files || [],
        },
        scheduleTime: draftMail?.scheduleTime || "",
        triggerId: draftMail?.triggerId || uuidv4(),
      });
    }
  };

  const cleanMemberList = () => {
    setCCOrgList((prev) => {
      return prev?.map((item) => ({
        ...item,
        checked: false,
      }));
    });

    setMemberOrgList((prev) => {
      return prev?.map((item) => ({
        ...item,
        checked: item?.objectId === senderId,
      }));
    });
  };

  const fetchRecipient = async () => {
    if (!isReplyAll) {
      setRecipientIds([]);
    }

    if (isReplyAll && letterId) {
      const result = await apiFactory.letterApi.getAllMemberIds(letterId);
      setRecipientIds(result?.data);
    }
  };

  useEffect(() => {
    init();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const pathname = location.pathname;

    if (pathname === "/conversation/draft-mail" && !selectedConversation) {
      setSelectedConversation({ type: "DRAFT" });
      setIsModalAddMember(false);
    }
  }, [location.pathname, selectedConversation]);

  useEffect(() => {
    if (originalOrgList?.length > 0) {
      const updatedMemberList = cloneDeep(
        searchOrganization(originalOrgList, null, null, false),
      )?.map((item) => ({
        ...item,
        checked:
          item?.objectId === recipent ||
          item?.objectId === senderId ||
          recipientIds?.includes(item?.objectId) ||
          draftMessage?.recipientIds?.includes(item?.objectId) ||
          replyMessage?.draftMail?.recipientIds?.includes(item?.objectId),
      }));

      const updatedCCList = cloneDeep(
        searchOrganization(originalOrgList, null, null, false),
      )?.map((item) => ({
        ...item,
        checked:
          draftMessage?.ccIds?.includes(item?.objectId) ||
          replyMessage?.draftMail?.ccIds?.includes(item?.objectId),
      }));

      setMemberOrgList(updatedMemberList);
      setCCOrgList(updatedCCList);
    }
  }, [
    letterId,
    recipent,
    originalOrgList?.length,
    senderId,
    draftMessage?.recipientIds,
    draftMessage?.ccIds,
    replyMessage?.draftMail?.recipientIds,
    replyMessage?.draftMail?.ccIds,
    recipientIds,
  ]);

  useEffect(() => {
    fetchRecipient();
  }, [isReplyAll, letterId]);

  const values = useMemo(
    () => ({
      topMessageList,
      selectedConversation,
      setIsReply,
      isReply,
      title,
      setTitle,
      cCOrgList,
      setCCOrgList,
      memberOrgList,
      setMemberOrgList,
      setTopMessageList,
      isModalLabelGroup,
      setIsModalLabelGroup,
      setLastMessageTs,
      isLoading,
      setIsLoading,
      setIsOpenModalDeleteConversation,
      isOpenModalDeleteConversation,
      isNotification,
      setIsNotification,
      draftMessage,
      cleanMemberList,
      isReplyAll,
      setIsReplyAll,
    }),
    [
      topMessageList,
      selectedConversation,
      isReply,
      title,
      cCOrgList,
      memberOrgList,
      isModalLabelGroup,
      lastMessageTs,
      isLoading,
      isOpenModalDeleteConversation,
      isNotification,
      draftMessage,
      setDraftMessage,
      isReplyAll,
    ],
  );

  return (
    <DraftMailContext.Provider value={values}>
      {children}
    </DraftMailContext.Provider>
  );
};
